import React from "react";
import { Link } from "react-router-dom";
import { withTranslation, type WithTranslation } from "react-i18next";
import { isAllowed } from "helper/acl";
import { getCompanyBrandClass } from "helper/company";
import { SUPPORTED_LANGUAGES } from "config/i18n";
import { useNotifications } from "api/queries/useNotifications";

import Navigation from "./Navigation";
import TopbarSearch from "./TopbarSearch";

const NavigationAny = Navigation as unknown as React.ComponentType<Record<string, unknown>>;
const TopbarSearchAny = TopbarSearch as unknown as React.ComponentType<Record<string, unknown>>;
import type { NotificationItem } from "../../types/state";

interface TopbarUser {
  first_name?: string;
  last_name?: string;
  real_user?: TopbarUser;
  customers?: unknown[];
  [key: string]: unknown;
}

interface TopbarCustomer {
  id?: number | string;
  company_name?: string;
  unread_notification_count?: number | string;
}

interface TopbarOwnProps extends WithTranslation {
  customer: TopbarCustomer;
  user: TopbarUser;
  logout: (event: React.MouseEvent) => void;
  unsetCustomer: (event: React.MouseEvent) => void;
  location?: { pathname: string };
}

interface TopbarStateProps {
  notifications: NotificationItem[];
  notificationsLoaded: boolean;
}

type TopbarProps = TopbarOwnProps & TopbarStateProps;

function isUnreadNotification(notification: NotificationItem) {
  return notification.read !== true && notification.read !== 1 && notification.read !== "1";
}

function Topbar({
  t,
  i18n,
  customer,
  user,
  logout,
  unsetCustomer,
  location,
  notifications,
  notificationsLoaded,
}: TopbarProps) {
  const [isAccountMenuOpen, setIsAccountMenuOpen] = React.useState(false);

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  const openAccountMenu = () => {
    setIsAccountMenuOpen((prev) => (!prev ? true : prev));
  };

  const closeAccountMenu = () => {
    setIsAccountMenuOpen((prev) => (prev ? false : prev));
  };

  const toggleAccountMenu = () => {
    setIsAccountMenuOpen((prev) => !prev);
  };

  const handleAccountMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      closeAccountMenu();
    }
  };

  const renderLanguageSwitcher = () => {
    return (
      <div
        className="Topbar-languageSwitcher"
        role="group"
        aria-label={t("topbar.language_switcher_aria")}
      >
        {SUPPORTED_LANGUAGES.map((language) => (
          <button
            key={language}
            type="button"
            className={
              "Topbar-languageButton " + (i18n.resolvedLanguage === language ? "isActive" : "")
            }
            onClick={() => changeLanguage(language)}
            aria-pressed={i18n.resolvedLanguage === language}
          >
            {t("topbar.language_" + language)}
          </button>
        ))}
      </div>
    );
  };

  const customerName = customer.company_name || "";
  const companyBrandClass = getCompanyBrandClass(customerName);
  const currentUser: TopbarUser = user.real_user || user;
  const currentUserName = `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim();
  const customerInitials = (
    (currentUser.first_name || "").charAt(0) + (currentUser.last_name || "").charAt(0)
  ).toUpperCase();
  const unreadNotifications = notificationsLoaded
    ? notifications.filter(isUnreadNotification).length
    : parseInt(String(customer.unread_notification_count || 0), 10);
  const notificationLabel =
    unreadNotifications > 0
      ? t("topbar.notifications_aria", { count: unreadNotifications })
      : t("topbar.notifications_aria_zero");

  return (
    <header className="Topbar Topbar--main" role="banner">
      <div className="Topbar-container Page-content">
        <div className="Topbar-primary">
          <div className="Topbar-brand">
            <Link
              className="Topbar-logoLink"
              to={BASE_PATH + "/"}
              aria-label={t("topbar.logo_aria")}
            >
              <div className={"LogoCompany Topbar-companyLogo isTiny is" + companyBrandClass}></div>
              <span className="Topbar-kicker">{t("topbar.platform_label")}</span>
            </Link>
          </div>

          <div className="Topbar-actions">
            {renderLanguageSwitcher()}

            <TopbarSearchAny />

            {isAllowed("webservice.notification") && (
              <Link
                to={BASE_PATH + "/notification"}
                className="Topbar-notification"
                aria-label={notificationLabel}
              >
                <span
                  className="Topbar-notificationIcon icon-notifications"
                  aria-hidden="true"
                ></span>
                <span className="Topbar-notificationCount" aria-hidden="true">
                  {unreadNotifications}
                </span>
              </Link>
            )}

            <div
              className={"Topbar-accountMenu" + (isAccountMenuOpen ? " isOpen" : "")}
              onMouseEnter={openAccountMenu}
              onMouseLeave={closeAccountMenu}
            >
              <button
                type="button"
                className="Topbar-account Topbar-accountTrigger"
                aria-haspopup="menu"
                aria-expanded={isAccountMenuOpen}
                aria-label={t("topbar.account_menu_aria", { name: currentUserName })}
                onClick={toggleAccountMenu}
                onKeyDown={handleAccountMenuKeyDown}
              >
                <div className="Topbar-avatar" aria-hidden="true">
                  {customerInitials || "U"}
                </div>
                <div className="Topbar-accountInfo">
                  <strong className="Topbar-accountName">{currentUserName}</strong>
                  <span className="Topbar-accountMeta">
                    {t("topbar.account_meta", { id: customer.id })}
                  </span>
                </div>
                <span className="Topbar-accountArrow" aria-hidden="true"></span>
              </button>

              <div
                className="Topbar-accountDropdown"
                role="menu"
                aria-label={t("topbar.account_actions_aria")}
                hidden={!isAccountMenuOpen}
                tabIndex={-1}
                onKeyDown={handleAccountMenuKeyDown}
              >
                <Link
                  to={BASE_PATH + "/profile"}
                  className="Navigation-dropdownLink"
                  role="menuitem"
                  onClick={closeAccountMenu}
                >
                  {t("profile.tabs.profil")}
                </Link>

                {(user.customers || []).length > 1 ? (
                  <button
                    type="button"
                    className="Topbar-accountDropdownButton"
                    role="menuitem"
                    onClick={(event) => {
                      closeAccountMenu();
                      unsetCustomer(event);
                    }}
                  >
                    {t("sidebar.label_change_account")}
                  </button>
                ) : (
                  ""
                )}

                {isAllowed("webservice.delegate") && (
                  <Link
                    to={BASE_PATH + "/profile/delegate/list"}
                    className="Navigation-dropdownLink"
                    role="menuitem"
                    onClick={closeAccountMenu}
                  >
                    {t("profile.tabs.delegate")}
                  </Link>
                )}
                <Link
                  to={BASE_PATH + "/profile/parameters"}
                  className="Navigation-dropdownLink"
                  role="menuitem"
                  onClick={closeAccountMenu}
                >
                  {t("profile.tabs.parameters")}
                </Link>

                <button
                  type="button"
                  className="Topbar-accountDropdownButton"
                  role="menuitem"
                  onClick={(event) => {
                    closeAccountMenu();
                    logout(event);
                  }}
                >
                  {t("sidebar.label_disconnect")}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="Topbar-secondary">
          <NavigationAny location={location} userData={user} customerData={customer} />
        </div>
      </div>
    </header>
  );
}

function withNotificationData(
  Component: React.ComponentType<TopbarProps>
): React.ComponentType<TopbarOwnProps> {
  return function TopbarNotificationData(props: TopbarOwnProps) {
    const notificationsQuery = useNotifications(props.customer?.id);

    return (
      <Component
        {...props}
        notifications={notificationsQuery.notifications}
        notificationsLoaded={notificationsQuery.notificationsLoaded}
      />
    );
  };
}

export default withTranslation()(withNotificationData(Topbar));
